import { useEffect, useState } from 'react';

import AddFavorite from 'components/AddFavorite';
import { ResourceTile } from 'components/ResourceTile/ResourceTile';
import WebsiteSplitter from 'components/WebsiteSplitter';

import APIHandler from 'config/APIHandler';

import useAppStorage from 'hooks/useAppStorage';
import { useSettings } from 'hooks/useSettings';

import { animated, useTrail } from '@react-spring/web';
import clsx from 'clsx';
import i18n from 'i18n-react';

interface ToolboxItem {
  type: string;
  title: string;
  subtitle: string;
  url: string;
  itemID: string | number;
  image: string;
  graduated_unlock_only: boolean;
  full_complete_unlock_only: boolean;
}

interface ChapterProgress {
  progress: number;
  total_pages: number;
  last_page: number;
  percentage?: number;
}

interface AppStorageUser {
  email: string;
  training_graduated?: string | null;
  [key: string]: unknown;
}

interface UserProgressResponse {
  status?: string;
  data: { chapters: ChapterProgress[] }[];
}

interface ToolboxItemProps {
  tool: ToolboxItem;
  appId: string | number;
  user: AppStorageUser | null;
  progress: number;
}

function ToolboxItem({ tool, appId, user, progress }: ToolboxItemProps) {
  const { type, title, subtitle, image } = tool;
  const itemID = String(tool.itemID);

  const url: string | string[] =
    typeof tool.url === 'string' && tool.url.includes(',') ? tool.url.split(',') : tool.url;

  const lockReason = (() => {
    switch (true) {
      case tool.full_complete_unlock_only && progress < 100:
        return i18n.translate('toolbox.completeAll') as string;
      case tool.graduated_unlock_only && !!user && !user.training_graduated:
        return i18n.translate('toolbox.completion') as string;
      default:
        return null;
    }
  })();

  const locked = lockReason !== null;
  const lockedText: React.ReactNode = lockReason ?? '';
  return (
    <div className="toolbox__Item">
      {!locked && <AddFavorite appId={appId} viewType="t" upID={itemID} />}
      <ResourceTile
        type={type}
        url={url}
        title={title}
        subtitle={subtitle}
        image={image}
        appId={appId}
        uniqueId={itemID}
        locked={locked}
        lockedText={lockedText}
      />
      {type === 'multi' && <WebsiteSplitter itemID={itemID} appID={appId} type="toolbox" />}
      {url === '#noContent' && (
        <div id="noContent" style={{ display: 'none' }}>
          <h1>{i18n.translate('errors.comingSoon')}</h1>
          <p>{i18n.translate('errors.missingContent')}</p>
        </div>
      )}
    </div>
  );
}

export default function Toolbox() {
  const { loaded: settingsLoaded, salesCompanionAppId } = useSettings();
  const { user } = useAppStorage() as { user: AppStorageUser | null };

  const [toolboxItems, setToolboxItems] = useState<ToolboxItem[]>([]);
  const [progress, setProgress] = useState(0);
  const [toolboxFetchComplete, setToolboxFetchComplete] = useState(false);

  const appId = import.meta.env.VITE_APP_ID;
  const getUserProgress = APIHandler.getUserProgress as (
    appId: string | number,
    email: string,
  ) => Promise<UserProgressResponse>;
  const getToolbox = APIHandler.getToolbox as (appId: string | number) => Promise<ToolboxItem[] | null>;

  useEffect(() => {
    if (!user || !settingsLoaded) return;

    void getUserProgress(appId, user.email).then((userProgress) => {
      if (userProgress.status === 'error') return;

      const chapters = userProgress.data[0]?.chapters ?? [];

      chapters.forEach((chapter) => {
        if (chapter.progress > 0 && chapter.total_pages > 0) {
          chapter.percentage = Math.floor((chapter.progress / (chapter.total_pages - 1)) * 100);
          if (chapter.percentage > 100) chapter.percentage = 100;
          if (Math.floor((chapter.total_pages / chapter.last_page) * 100) === 100) {
            chapter.percentage = 100;
          }
        }
        chapter.percentage ??= 0;
      });

      const total = chapters.reduce((sum, c) => sum + (c.percentage ?? 0), 0);
      setProgress(total / chapters.length);
    });
  }, [user, settingsLoaded, appId, getUserProgress]);

  useEffect(() => {
    if (!settingsLoaded) return;
    const toolboxAppId = salesCompanionAppId ?? appId;

    void getToolbox(toolboxAppId)
      .then((res) => {
        if (res) setToolboxItems(res);
        setToolboxFetchComplete(true);
      })
      .catch(() => {
        setToolboxFetchComplete(true);
      });
  }, [appId, getToolbox, salesCompanionAppId, settingsLoaded]);

  const trail = useTrail(toolboxItems.length, {
    from: { opacity: 0, transform: 'translateY(20%)' },
    to: { opacity: 1, transform: 'translateY(0%)' },
    immediate: import.meta.env.MODE === 'test',
  });

  return (
    <div
      className="toolbox__Grid"
      data-testid="toolbox-grid"
      data-fetch-complete={toolboxFetchComplete ? 'true' : 'false'}
    >
      {trail.map((style, id) => {
        const tool = toolboxItems[id];
        if (!tool?.type) return null;

        const isLast = id === trail.length - 1;
        const testId = isLast ? 'toolbox-card-last' : `toolbox-card-${id}`;

        return (
          <animated.div
            key={id}
            className={clsx('ToolboxCard__Animated', { 'toolbox__card--directory': tool.type === 'directory' })}
            data-testid={testId}
            style={style}
          >
            <ToolboxItem tool={tool} appId={appId} user={user} progress={progress} />
          </animated.div>
        );
      })}
    </div>
  );
}
